// src/utils/fetchWrapper.js
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9500/api/v1";

const instance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // <-- ensures cookies are sent & stored
  headers: {
    "Content-Type": "application/json",
  },
});

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

// Backend requires X-CSRF-Token on every non-GET request (except a few exempt
// auth/webhook paths) — attach it from the readable csrfToken cookie set at login.
instance.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrfToken = getCookie("csrfToken");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

const clearSessionAndRedirect = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  localStorage.removeItem("permissions");
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
};

// A dead/expired session (JWT expiry, invalidated cookie, stale CSRF pairing)
// otherwise fails silently per-request, leaving the app half-rendered until
// someone manually clears cookies. 401 is always an auth failure; 403 is also
// used for legitimate permission checks, so only treat CSRF-specific 403s as
// a dead session — a plain "you're not an admin" 403 must not log the user out.
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || "";
    const isCsrfFailure = status === 403 && /csrf/i.test(message);
    if (status === 401 || isCsrfFailure) {
      clearSessionAndRedirect();
    }
    return Promise.reject(error);
  }
);

export const fetchWrapper = {
  get: (url, config = {}) => instance.get(url, config),
  post: (url, data, config = {}) => instance.post(url, data, config),
  put: (url, data, config = {}) => instance.put(url, data, config),
  delete: (url, config = {}) => instance.delete(url, config),
};
